import { TokenAmount } from "@/components/brand/TokenAmount";
import { TokenIcon, TokenIconRow } from "@/components/brand/TokenIcon";
import { RiskGauge } from "@/components/defi/RiskGauge";
import { Button } from "@/components/ui/Button";
import { InfoExplain } from "@/components/ui/InfoExplain";
import type { VaultActionKind } from "@/components/vault/VaultDetailPanel";
import {
	riskBandOrder,
	rowBgForBand,
	VaultFiltersBar,
	type BandFilter,
} from "@/components/vault/VaultFiltersBar";
import { VaultRiskBadge } from "@/components/vault/VaultRiskBadge";
import {
	VaultViewToggle,
	type VaultViewMode,
} from "@/components/vault/VaultViewToggle";
import {
	toRiskParams,
	useProtocolRiskParams,
} from "@/hooks/usePublicProtocol";
import { useUserVaults, type UiVault } from "@/hooks/useUserVaults";
import { useAuth } from "@/lib/auth";
import { maxLtvFromCr } from "@/lib/defi-math";
import { hasProtocolEnv } from "@/lib/protocol-actor";
import { ROUTES } from "@/lib/routes";
import {
	classifyVaultRisk,
	formatCr,
	type ProtocolRiskParams,
} from "@/lib/vault-risk";
import { cn, formatNum, formatUsd } from "@/lib/utils";

function formatCreated(ns?: bigint | null): string {
	if (ns == null || ns <= 0n) return "—";
	const ms = Number(ns / 1_000_000n);
	if (!Number.isFinite(ms) || ms <= 0) return "—";
	return new Date(ms).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}
import {
	ArrowDownToLine,
	ExternalLink,
	Flame,
	Landmark,
	Plus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

function collKeys(vault: UiVault): string[] {
	return vault.raw?.collateral.map(([k]) => k) ?? [];
}

const ACTIONS: {
	key: Exclude<VaultActionKind, null>;
	label: string;
	icon: typeof Plus;
}[] = [
	{ key: "deposit", label: "Deposit", icon: Plus },
	{ key: "withdraw", label: "Withdraw", icon: ArrowDownToLine },
	{ key: "mint", label: "Mint", icon: Landmark },
	{ key: "repay", label: "Repay", icon: Flame },
];

function OwnedVaultCard({
	vault,
	params,
	onOpen,
	onAction,
}: {
	vault: UiVault;
	params: ProtocolRiskParams;
	onOpen: () => void;
	onAction: (a: Exclude<VaultActionKind, null>) => void;
}) {
	const risk = classifyVaultRisk(vault.collateralUsd, vault.debtUsdm, params);
	const keys = collKeys(vault);
	const maxLtv = maxLtvFromCr(params.minCollateralRatio);

	return (
		<div
			className={cn(
				"vault-card-glass flex h-full min-w-0 flex-col gap-2 p-3.5",
				risk.borderClass,
				risk.bgClass,
			)}
		>
			{/* Max 3 content rows for denser grid + more horizontal width */}
			<button type="button" className="w-full min-w-0 text-left" onClick={onOpen}>
				{/* Row 1: id + coll icons + risk */}
				<div className="flex items-center justify-between gap-2">
					<div className="flex min-w-0 items-center gap-2">
						<p className="shrink-0 font-mono text-base font-bold">
							#{vault.id.toString()}
						</p>
						{keys.length > 0 ? (
							<TokenIconRow tokens={keys} size="sm" />
						) : null}
						<p className="truncate text-[11px] text-muted-foreground">
							{vault.collateralLabel || "Empty"}
						</p>
					</div>
					<VaultRiskBadge meta={risk} size="sm" showFactor={false} />
				</div>
				{/* Row 2: compact metrics — value | debt | Health */}
				<div className="mt-2 grid grid-cols-3 gap-2 text-sm">
					<div className="min-w-0">
						<p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
							Value
						</p>
						<p className="truncate font-mono-amount text-sm font-bold tabular-nums">
							{formatUsd(vault.collateralUsd, 0)}
						</p>
					</div>
					<div className="min-w-0">
						<p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
							Debt
						</p>
						<p className="truncate font-mono-amount text-sm font-bold tabular-nums">
							{formatNum(vault.debtUsdm, vault.debtUsdm >= 100 ? 0 : 2)}
							<span className="ml-0.5 text-[10px] font-semibold text-muted-foreground">
								USDM
							</span>
						</p>
					</div>
					<div className="min-w-0">
						<p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
							{risk.band === "liquidatable" || risk.band === "underwater"
								? "Status"
								: "Health"}
						</p>
						<p className={cn("truncate font-mono-amount text-sm font-bold", risk.factorClass)}>
							{risk.band === "liquidatable"
								? "liquidatable"
								: risk.band === "underwater"
									? "underwater"
									: formatCr(risk.cr)}
						</p>
					</div>
				</div>
				{/* Row 3: slim risk gauge — Health center */}
				<div className="mt-1.5 flex justify-center">
					<RiskGauge
						ltv={vault.ltvPct}
						healthFactor={risk.cr}
						maxLtv={maxLtv}
						mintCr={params.minCollateralRatio}
						liquidationCr={params.liquidationThreshold}
						size="sm"
						centerMetric="health"
					/>
				</div>
			</button>
			{/* Actions: single dense row of 4 when wide enough, wrap on narrow */}
			<div className="mt-auto grid grid-cols-4 gap-1 border-t border-border/50 pt-2">
				{ACTIONS.map(({ key, label, icon: Icon }) => (
					<button
						key={key}
						type="button"
						title={label}
						className="inline-flex min-h-9 min-w-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-border bg-background/40 px-0.5 text-[10px] font-bold hover:border-primary/50 hover:text-primary sm:flex-row sm:gap-1 sm:text-[11px]"
						onClick={() => onAction(key)}
					>
						<Icon className="size-3 shrink-0" aria-hidden />
						<span className="truncate">{label}</span>
					</button>
				))}
			</div>
		</div>
	);
}

function OwnedVaultsTable({
	vaults,
	params,
	onOpen,
}: {
	vaults: UiVault[];
	params: ProtocolRiskParams;
	onOpen: (id: bigint) => void;
}) {
	const liq = params.liquidationThreshold;
	const minCr = params.minCollateralRatio;

	return (
		<div className="card-surface overflow-x-auto">
			<table className="w-full min-w-[920px] text-left text-sm">
				<thead className="sticky top-0 bg-card/95 backdrop-blur">
					<tr className="border-b border-border text-[11px] uppercase text-muted-foreground">
						<th className="px-3 py-2 font-semibold">Vault</th>
						<th className="px-3 py-2 font-semibold">Created</th>
						<th className="px-3 py-2 font-semibold">Collateral</th>
						<th className="px-3 py-2 font-semibold">Value (USD)</th>
						<th className="px-3 py-2 font-semibold">Debt</th>
						<th className="relative px-3 py-2 font-semibold">
							<InfoExplain label="LTV" title="Loan to value (LTV)">
								<p>
									LTV is the share of your collateral already borrowed as
									USDM debt. Formula: debt ÷ collateral value in USD.
								</p>
								<p>
									Example: $100 collateral and $50 debt → LTV 50%. Higher LTV
									means more risk. Liquidation risk rises near{" "}
									{(100 / liq).toFixed(0)}% LTV (CR {liq.toFixed(2)}×).
								</p>
							</InfoExplain>
						</th>
						<th className="relative px-3 py-2 font-semibold">
							<InfoExplain
								label="CR"
								title="Collateral ratio (CR)"
							>
								<p>
									CR tells you how many dollars of collateral back each $1 of
									USDM debt. Formula: collateral USD ÷ debt.
								</p>
								<p>
									Example: CR 1.50× means $150 of collateral for every $100
									debt — a 50% buffer. Mint floor {minCr.toFixed(2)}× (yellow
									caution / orange at-risk below it). Liquidatable below{" "}
									{liq.toFixed(2)}× (red); underwater below 1.00× (purple).
								</p>
								<p>
									Health factor in this protocol is the same number as CR
									(higher is safer).
								</p>
							</InfoExplain>
						</th>
						<th className="px-3 py-2 font-semibold">Status</th>
						<th className="px-3 py-2 font-semibold">Open</th>
					</tr>
				</thead>
				<tbody>
					{vaults.map((v) => {
						const risk = classifyVaultRisk(
							v.collateralUsd,
							v.debtUsdm,
							params,
						);
						return (
							<tr
								key={String(v.id)}
								className={cn(
									"cursor-pointer border-b border-border/40 transition hover:brightness-95",
									rowBgForBand(risk.band),
									risk.borderClass,
								)}
								onClick={() => onOpen(v.id)}
							>
								<td className="px-3 py-2 font-mono text-xs font-bold">
									#{v.id.toString()}
								</td>
								<td className="px-3 py-2 text-xs text-muted-foreground">
									{formatCreated(v.createdNs)}
								</td>
								<td className="max-w-[10rem] px-3 py-2 text-xs">
									<div className="flex items-center gap-2">
										{collKeys(v).length > 0 ? (
											<span className="flex items-center gap-0.5">
												{collKeys(v).map((k) => (
													<TokenIcon key={k} token={k} size="sm" plain />
												))}
											</span>
										) : null}
										<span className="truncate">
											{v.collateralLabel || "Empty"}
										</span>
									</div>
								</td>
								<td className="px-3 py-2 font-mono-amount text-xs font-semibold">
									{formatUsd(v.collateralUsd, 0)}
								</td>
								<td className="px-3 py-2">
									<TokenAmount
										id="USDM"
										value={v.debtUsdm}
										decimals={2}
										size="sm"
									/>
								</td>
								<td className="px-3 py-2 font-mono-amount text-xs">
									{formatNum(v.ltvPct, 1)}%
								</td>
								<td
									className={cn(
										"px-3 py-2 font-mono-amount text-xs font-bold",
										risk.factorClass,
									)}
								>
									{formatCr(risk.cr)}
								</td>
								<td className="px-3 py-2">
									<VaultRiskBadge meta={risk} size="sm" showFactor={false} />
								</td>
								<td className="px-3 py-2">
									<button
										type="button"
										className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-border bg-background/80 px-2.5 text-[11px] font-bold hover:border-primary hover:text-primary"
										onClick={(e) => {
											e.stopPropagation();
											onOpen(v.id);
										}}
									>
										<ExternalLink className="size-3.5" aria-hidden />
										Details
									</button>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

/** Your vaults: create CTA, list table / grid cards, actions open detail. */
export function VaultsSection({ showHeader = true }: { showHeader?: boolean }) {
	const { isAuthenticated, isLoggingIn, login, sessionStatus, reportSessionIssue } =
		useAuth();
	const navigate = useNavigate();
	const { data: vaults = [], isLoading, isError, error } = useUserVaults();
	const [view, setView] = useState<VaultViewMode>("list");
	const [band, setBand] = useState<BandFilter>("all");
	const [showEmpty, setShowEmpty] = useState(false);
	const { data: riskData } = useProtocolRiskParams();
	const riskParams = toRiskParams(riskData);
	const envOk = hasProtocolEnv();

	const filtered = useMemo(() => {
		let list = vaults.map((v) => {
			const risk = classifyVaultRisk(v.collateralUsd, v.debtUsdm, riskParams);
			return { v, risk };
		});
		if (!showEmpty) {
			list = list.filter(({ risk }) => risk.band !== "empty");
		}
		if (band !== "all") {
			list = list.filter(({ risk }) => risk.band === band);
		}
		list.sort((a, b) => {
			const d = riskBandOrder(a.risk.band) - riskBandOrder(b.risk.band);
			if (d !== 0) return d;
			return a.risk.cr - b.risk.cr;
		});
		return list.map(({ v }) => v);
	}, [vaults, band, showEmpty, riskParams]);

	const openVault = (
		id: bigint | string,
		action: VaultActionKind = null,
	) => {
		const path = ROUTES.vaultDetail(id);
		if (action) navigate(`${path}?action=${action}`);
		else navigate(path);
	};

	function goBorrowWizard() {
		if (!isAuthenticated) {
			void login();
			return;
		}
		if (!envOk) {
			toast.error(
				"Protocol env not baked into this SPA. Re-run: bash scripts/start-local.sh --spa v2",
			);
			return;
		}
		navigate(`${ROUTES.borrow}?from=vaults`);
	}

	const sessionBroken =
		sessionStatus === "degraded" ||
		sessionStatus === "degraded_prompt" ||
		sessionStatus === "expired";

	return (
		<div className="space-y-4">
			{showHeader ? (
				<div className="flex flex-wrap items-end justify-between gap-3">
					<div>
						<p className="section-eyebrow">Your positions</p>
						<h2 className="text-lg font-bold">Your vaults</h2>
						<p className="text-sm text-muted-foreground">
							List view by default. Create via Borrow (deposit collateral),
							then mint, withdraw, or repay.
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<Button
							size="sm"
							variant="primary"
							loading={isLoggingIn}
							onClick={() => goBorrowWizard()}
						>
							Create vault
						</Button>
						<VaultViewToggle view={view} onChange={setView} />
					</div>
				</div>
			) : (
				<div className="flex justify-end gap-2">
					<Button
						size="sm"
						loading={isLoggingIn}
						onClick={() => goBorrowWizard()}
					>
						Create vault
					</Button>
					<VaultViewToggle view={view} onChange={setView} />
				</div>
			)}

			{!envOk ? (
				<div className="card-surface p-6 text-center text-sm text-muted-foreground">
					Protocol canister not configured. Check SPA env (
					<code className="font-mono text-xs">VITE_CANISTER_ID_PROTOCOL</code>
					).
				</div>
			) : !isAuthenticated ? (
				<div className="card-surface p-6 text-center">
					<p className="text-sm text-muted-foreground">
						Connect to load your vaults.
					</p>
					<Button
						className="mt-4"
						variant="primary"
						loading={isLoggingIn}
						onClick={() => void login()}
					>
						Connect
					</Button>
				</div>
			) : isLoading ? (
				<div
					className={cn(
						"grid gap-3",
						view === "grid"
							? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
							: "grid-cols-1",
					)}
				>
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className="skeleton-shimmer h-40" />
					))}
				</div>
			) : isError || sessionBroken ? (
				<div className="card-surface space-y-3 p-6 text-center">
					<p className="text-sm text-danger">
						{error instanceof Error
							? error.message
							: sessionBroken
								? "Session issue — your vaults may still exist. Reconnect to load them."
								: "Failed to load vaults"}
					</p>
					<p className="text-xs text-muted-foreground">
						This is not an empty portfolio. Log in again or reconnect protocol.
					</p>
					<Button
						variant="primary"
						onClick={() => reportSessionIssue("degraded")}
					>
						Fix session
					</Button>
				</div>
			) : vaults.length === 0 ? (
				<div className="card-surface p-6 text-center">
					<p className="text-sm text-muted-foreground">
						No vaults yet. Open the Borrow wizard to deposit collateral and
						mint USDM — that creates your vault.
					</p>
					<Button className="mt-4" onClick={() => goBorrowWizard()}>
						Start borrow
					</Button>
				</div>
			) : (
				<>
					<VaultFiltersBar
						band={band}
						onBandChange={setBand}
						showSort={false}
						showEmptyToggle
						showEmpty={showEmpty}
						onShowEmptyChange={setShowEmpty}
					/>
					{filtered.length === 0 ? (
						<p className="py-6 text-center text-sm text-muted-foreground">
							No vaults match this filter.
						</p>
					) : view === "list" ? (
						<OwnedVaultsTable
							vaults={filtered}
							params={riskParams}
							onOpen={(id) => openVault(id)}
						/>
					) : (
						<ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{filtered.map((v) => (
								<li key={String(v.id)}>
									<OwnedVaultCard
										params={riskParams}
										vault={v}
										onOpen={() => openVault(v.id)}
										onAction={(a) => openVault(v.id, a)}
									/>
								</li>
							))}
						</ul>
					)}
				</>
			)}
		</div>
	);
}
